/**
 * Контракты режима «Предложения» (ТЗ §6, режим 5).
 *
 * Режим переносит навык в естественный текст: пользователь набирает
 * предложение целиком, со знаками препинания и заглавными буквами. Разбиение
 * на слова здесь было бы вредно — именно переходы между словами, пробелы и
 * пунктуация отличают реальный текст от списка слов (ТЗ §4.6).
 */

import type {
  HintLevel,
  LanguageCode,
  LayoutId,
  MasteryStat,
  MasteryUnit,
  MasteryUnitId,
  TypingError,
  TypingMetrics,
} from '@type2x/shared-contracts';
import type { RunObservation } from '@type2x/typing-engine';

/** Снимок навыка, из которого подбираются предложения. */
export interface SentenceSnapshot {
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  readonly units: Readonly<Record<string, MasteryUnit>>;
  readonly stats: Readonly<Record<string, MasteryStat>>;
}

/** Почему предложение попало в набор. */
export const SENTENCE_REASONS = ['weak', 'due', 'transfer'] as const;
export type SentenceReason = (typeof SENTENCE_REASONS)[number];

/** Одно предложение сессии. */
export interface SentenceDrill {
  readonly text: string;
  /**
   * Учебная единица, ради которой предложение выбрано, либо null.
   *
   * Предложение без единицы — материал переноса: оно не привязано к слабому
   * месту и служит проверкой того, что навык работает на обычном тексте.
   */
  readonly unitId: MasteryUnitId | null;
  /** Слово единицы в начальной форме либо null. */
  readonly word: string | null;
  readonly reason: SentenceReason;
  /** Пройдено ли предложение. */
  readonly done: boolean;
  /** Наблюдения по единице за это предложение. */
  readonly observed: RunObservation;
}

/** Сессия режима «Предложения». */
export interface SentencesSession {
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  readonly drills: readonly SentenceDrill[];
  readonly currentIndex: number;
  /** Все предложения пройдены. */
  readonly finished: boolean;
  /** Суммарные метрики набора за сессию. */
  readonly metrics: TypingMetrics;
  /** Ошибки всех прогонов: из них собирается сводка по типам для сервера. */
  readonly errors: readonly TypingError[];
}

/** Прогон одного предложения. */
export interface SentenceRun {
  readonly observation: RunObservation;
  readonly metrics: TypingMetrics;
  readonly errors: readonly TypingError[];
}

/** Задание: что показать и набрать. */
export interface SentenceTask {
  readonly text: string;
  /** Слово, ради которого предложение подобрано, либо null. */
  readonly word: string | null;
  readonly reason: SentenceReason;
  readonly hintLevel: HintLevel;
}

/** Сколько предложений берётся в одну сессию. */
export const SENTENCES_PER_SESSION = 5;

/**
 * Уровень подсказок режима.
 *
 * Клавиатура скрыта: смысл режима — печатать текст, а не искать клавиши.
 * Исправления при этом разрешены, в отличие от контрольного теста: здесь
 * пользователь учится, а не сдаёт (ТЗ §7.5, §7.6).
 */
export const SENTENCE_HINT_LEVEL: HintLevel = 'keyboard-hidden';
